import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable, BehaviorSubject, of, combineLatest } from 'rxjs';
import { Project } from '../../../project/models/project';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { ConfigurationDataService } from '../../services/configuration-data.service';
import { map, tap, switchMap, take } from 'rxjs/operators';
import { TrainTypeCreateComponent } from '../../components/train-type-create/train-type-create.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-train-type-configuration',
  template: require('./train-type-configuration.component.html'),
  styles: [require('./train-type-configuration.component.sass')]
})
export class TrainTypeConfigurationComponent implements OnInit {

  @ViewChild(TrainTypeCreateComponent) trainTypeCreate: TrainTypeCreateComponent;

  projects$: Observable<Project[]>;
  trainTypes$: Observable<any[] | null>;
  projectId: number;
  configurations$: Observable<any[] | null>;

  trainType: string;
  activeConfig: any;

  latestConfig: any;

  private readonly refreshTrainTypeConfigurations$ = new BehaviorSubject(undefined);
  private readonly refreshTrainTypes$ = new BehaviorSubject(undefined);

  constructor(
    private projectDataService: ProjectDataService,
    private configurationDataService: ConfigurationDataService,
    private translate: TranslateService,
    private messageService: MessageService
  ) {
    this.projects$ = this.projectDataService.getAllProjects().pipe(
      take(1),
      map((projects) => projects.filter(project => project.id > 0)),
      tap((projects) => {
        this.projectId = this.projectId ? this.projectId : projects && projects[0] && projects[0].id;
      })
    );

    this.trainTypes$ = combineLatest(this.projects$, this.refreshTrainTypes$).pipe(
      switchMap(() => this.projectId ? this.configurationDataService.getTrainTypes(this.projectId) : of(null)),
      tap((trainTypes) => {
        this.trainType = trainTypes && trainTypes[0];
      })
    )

    this.configurations$ = combineLatest(this.trainTypes$,this.refreshTrainTypeConfigurations$).pipe(
      switchMap(() => this.projectId && this.trainType ? this.configurationDataService.getTrainTypeConfigurations(this.projectId, this.trainType) : of(null)),
      tap((configurations) => {
        this.activeConfig = configurations && configurations[0];
      })
    )

   }

  ngOnInit(): void {
    this.refreshTrainTypes$.next(undefined);
  }


  onDataChanged = (data: any) => {
    this.latestConfig = data ? {
      ...data
    } : undefined;
  }


  onProjectChange() : void {
    this.refreshTrainTypes$.next(undefined);
  }

  onTrainTypeChange() : void {
    this.refreshTrainTypeConfigurations$.next(undefined);
  }

  async createNewConfiguration(): Promise<void>{
    const newConfig: any = {...this.latestConfig || this.activeConfig};
    delete newConfig._id;
    newConfig.activatedOn = new Date().getTime();
    newConfig.createdOn = new Date().getTime();
    try {
      await this.configurationDataService.createTrainTypeConfiguration(this.projectId, this.trainType, newConfig).toPromise();
      this.refreshTrainTypeConfigurations$.next(undefined);
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_CONFIGURATION"), 
        detail: `${this.translate.instant("I18N_CONFIGURATION.NEW_CONFIGURATION")} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      window.location.reload();
    } catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_CONFIGURATION"), 
        detail: err.message
      });
    }

  }

  async viewConfig(configuration: any) : Promise<void> {
    this.activeConfig = {
      ...configuration
    }
  }

  async activateConfig(configuration: any) : Promise<void> {
    try {
      await this.configurationDataService.updateTrainTypeConfiguration(this.projectId, this.trainType,  configuration).toPromise();
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.ACTIVATE_CONFIGURATION"), 
        detail: `${this.translate.instant("I18N_CONFIGURATION.VERSION")} ${configuration.version} ${this.translate.instant("I18N_GENERAL.OF")} "${this.trainType}" ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_ACTIVATED")} `
      });
      this.refreshTrainTypeConfigurations$.next(undefined);
    }
    catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.ACTIVATE_CONFIGURATION"), 
        detail: err.message
      });
    }

  }

  createNewTrainType = async () => {
    await this.trainTypeCreate.showModal(this.projectId);
  }

  onCreateTrainType = async (data: {
    project: Project,
    trainType: string
  }) => {
    const project = data && data.project;
    const trainTypeName = data && data.trainType;
    const trainType = `${project.name}-${trainTypeName}`;
    const config = this.activeConfig ? {...this.activeConfig} : {};
    if (config) {
      delete config._id;
      delete config.activatedOn;
      delete config.createdOn;
    }
    config.projectId = project.id;
    config.type = trainType;
    try {
      await this.configurationDataService.createTrainTypeConfiguration(project.id, trainType, config).toPromise();
      this.messageService.add({
        severity:'success', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_TRAIN_TYPE"), 
        detail: `${this.translate.instant("I18N_GENERAL.TRAIN_TYPE")} "${trainType}" ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.refreshTrainTypes$.next(undefined);
    }
    catch (err) {
      this.messageService.add({
        severity:'error', 
        summary: this.translate.instant("I18N_CONFIGURATION.CREATE_NEW_TRAIN_TYPE"), 
        detail: err.message
      });
    }
  }

}
